import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetProjectLocaleKeyDataParams,
  GetProjectLocaleKeyDataData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetProjectLocaleKeyDataState
  extends MergeRequestState<GetProjectLocaleKeyDataData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getProjectLocaleKeyData,
  isFetching: (state: ReduxState) =>
    state.server.getProjectLocaleKeyData.isFetching,
  data: (state: ReduxState) => state.server.getProjectLocaleKeyData.data,
  error: (state: ReduxState) => state.server.getProjectLocaleKeyData.error,
};

const initialState: GetProjectLocaleKeyDataState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getProjectLocaleKeyData';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      config,
    }: { params: GetProjectLocaleKeyDataParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getProjectLocaleKeyData(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetProjectLocaleKeyData {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getProjectLocaleKeyData: ServerGetProjectLocaleKeyData = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
